import {useEffect, useMemo, useState} from 'react'
import {useLocation, useNavigate, Navigate} from 'react-router-dom'
import {Suspense} from 'react'
import {Preloader} from './components/common/preloader'
import ErrorBoundary from './components/layout/components/error-boundary'
import Approuter from './approuter'
import {ToastContainer} from 'react-toastify'
import {Toaster} from 'sonner'
import Layout from './components/layout/layout'
import {useLazyGetUserQuery} from './features/user/user'
import {useHandleRequest} from './hooks/use-handle-request'
import {useStorage} from './utils/storage'
import {LayoutExcludedRoutes} from './constants/layout-excluded-routes'
import {PrivateRoutes} from './constants/private-routes'
import {useRole} from './hooks/use-role'
import {USER_ROLES} from './constants/roles'

const App = () => {
  const navigate = useNavigate()
  const {role} = useRole()
  const handleRequest = useHandleRequest()
  const location = useLocation()
  const [getUser, {data: user, isFetching: userLoading}] = useLazyGetUserQuery()
  const [isLoading, setIsLoading] = useState(false)
  const isLayoutExcluded = useMemo(
    () => Object.values(LayoutExcludedRoutes).includes(location.pathname),
    [location.pathname],
  )

  const isPrivateRoute = useMemo(() => Object.values(PrivateRoutes).includes(location.pathname), [location.pathname])

  const fetchUser = async () => {
    await handleRequest({
      request: async () => {
        const res = await getUser()
        return res
      },
      onSuccess: (res) => {
        console.log('LOCATION', location.pathname)
        const userRole = res?.data?.role || res?.role || useStorage.getRole()
        const defaultDest = userRole === USER_ROLES.SALES_MANAGER ? '/clients' : '/dashboard'
        navigate(location.pathname === '/login' ? defaultDest : location.pathname, {replace: true})
      },
      onError: () => {
        useStorage.removeCredentials()
      },
    })
    setIsLoading(false)
  }

  useEffect(() => {
    const accessToken = useStorage.getTokens()?.access?.split(' ')[1]
    if (!user && accessToken) {
      fetchUser()
    } else {
      setIsLoading(false)
    }
  }, [isPrivateRoute])

  useEffect(() => {
    if (location.pathname === '/accountants' && role !== USER_ROLES.ADMIN) {
      const dest = role === USER_ROLES.SALES_MANAGER ? '/clients' : '/dashboard'
      navigate(dest)
    }
    if (location.pathname === '/dashboard' && role === USER_ROLES.SALES_MANAGER) {
      navigate('/clients')
    }
  }, [location.pathname, role])

  const renderContent = () => (
    <Suspense fallback={<Preloader />}>
      <ErrorBoundary>
        <Approuter />
        <ToastContainer position="top-center" autoClose={3000} hideProgressBar />
        <Toaster position="top-center" richColors />
      </ErrorBoundary>
    </Suspense>
  )

  if (isLoading) {
    return ''
  }

  if (isPrivateRoute && !user) {
    return <Navigate to={'/login'} />
  }

  if (isLayoutExcluded) {
    return renderContent()
  }

  return <Layout>{renderContent()}</Layout>
}

export default App
